import { detectCombos, type PipValue } from './combos';
import { BOMB_BONUS, EMBER_BONUS, type DieKind } from './dice';
import { HANDS_PER_ROUND } from './run-constants';
import type { ComboResult } from './types';

/**
 * I Talismani — il cuore drogante (brief §4.3). 40 al v1 (8 per famiglia),
 * effetti puri e deterministici: la stessa mano dà sempre lo stesso punteggio.
 *
 * Pipeline di scoring (ordine fisso, documentato anche nei test):
 *   1. pips truccati (talismani + Dado di Vetro + regola boss "no-sei")
 *   2. combo migliore sui pips truccati (boss "minimo-tris" esclude le coppie)
 *   3. chips flat
 *   4. chips percentuali (alch-pietra)
 *   5. mult additivi
 *   6. mult moltiplicativi
 */
export type TalismanFamily = 'fissazioni' | 'pirotecnici' | 'spettrali' | 'alchemici' | 'rischio';
export type TalismanRarity = 'comune' | 'raro' | 'leggendario';

export type BossRule = 'no-sei' | 'pool-rilanci' | 'minimo-tris' | 'tremante';

export interface TalismanDef {
  id: string;
  family: TalismanFamily;
  rarity: TalismanRarity;
  price: number;
}

export const TALISMAN_PRICE: Record<TalismanRarity, number> = { comune: 4, raro: 7, leggendario: 12 };

const def = (id: string, family: TalismanFamily, rarity: TalismanRarity): TalismanDef => ({
  id,
  family,
  rarity,
  price: TALISMAN_PRICE[rarity],
});

export const TALISMANS: TalismanDef[] = [
  // Fissazioni — i numeri come ossessione
  def('fix-tre', 'fissazioni', 'comune'),           // i 3 usati valgono 6 pips
  def('fix-pari', 'fissazioni', 'comune'),          // +4 chips per dado pari usato
  def('fix-cinque', 'fissazioni', 'comune'),        // +5 chips per ogni 5 usato
  def('fix-dispari', 'fissazioni', 'comune'),       // +3 chips per dado dispari usato
  def('fix-mono', 'fissazioni', 'raro'),            // combo mono-faccia → +2 mult
  def('fix-scala', 'fissazioni', 'raro'),           // scale → +2 mult
  def('fix-doppietta', 'fissazioni', 'raro'),       // doppia coppia → +25 chips
  def('fix-tabellina', 'fissazioni', 'leggendario'),// combo di soli dadi pari → mult ×1.5
  // Pirotecnici — esplosioni
  def('pyro-sei', 'pirotecnici', 'comune'),         // +8 chips per ogni 6 usato
  def('pyro-polvere', 'pirotecnici', 'comune'),     // mano con tiro forzato → +20 chips
  def('pyro-lampo', 'pirotecnici', 'comune'),       // prima mano del round → +15 chips
  def('pyro-miccia', 'pirotecnici', 'raro'),        // chiudi al primo lancio → +2 mult
  def('pyro-catena', 'pirotecnici', 'raro'),        // +0.5 mult per rilancio usato
  def('pyro-castello', 'pirotecnici', 'raro'),      // full → +2 mult
  def('pyro-finale', 'pirotecnici', 'leggendario'), // combo base ≥120 → mult ×2
  def('pyro-apoteosi', 'pirotecnici', 'leggendario'),// quintetto/sestetto → mult ×3
  // Spettrali — dadi che non ci sono
  def('ghost-dado', 'spettrali', 'comune'),         // combo con coppia → +15 chips
  def('ghost-nebbia', 'spettrali', 'comune'),       // combo che non usa tutti i dadi → +10 chips
  def('ghost-sussurro', 'spettrali', 'comune'),     // +metà pips non usati ai chips
  def('ghost-eco', 'spettrali', 'raro'),            // il dado usato più alto conta due volte
  def('ghost-terzo', 'spettrali', 'raro'),          // tris → +30 chips
  def('ghost-specchi', 'spettrali', 'raro'),        // +4 chips per coppia sul tavolo
  def('ghost-presenza', 'spettrali', 'leggendario'),// +0.5 mult per talismano posseduto
  def('ghost-medium', 'spettrali', 'leggendario'),  // +1 mult per dado NON usato
  // Alchemici — trasformazione
  def('alch-metallo', 'alchemici', 'comune'),       // gli 1 usati valgono 7 pips
  def('alch-mercurio', 'alchemici', 'comune'),      // +1 pip per dado usato
  def('alch-fermento', 'alchemici', 'comune'),      // +2 chips per round completato
  def('alch-distillato', 'alchemici', 'raro'),      // combo con tutti e 6 i dadi → +1.5 mult
  def('alch-piombo-oro', 'alchemici', 'raro'),      // +6 chips per dado speciale usato
  def('alch-enzima', 'alchemici', 'raro'),          // combo di esattamente 4 dadi → +1.5 mult
  def('alch-pietra', 'alchemici', 'leggendario'),   // chips +25%
  def('alch-grande-opera', 'alchemici', 'leggendario'), // +0.75 mult per famiglia posseduta
  // Rischio — il TILT come stile di vita
  def('risk-freno', 'rischio', 'comune'),           // +1 mult per rilancio forzato
  def('risk-adrenalina', 'rischio', 'comune'),      // TILT ≥35 alla chiusura → +12 chips
  def('risk-cerino', 'rischio', 'comune'),          // ultima mano del round → +20 chips
  def('risk-limite', 'rischio', 'raro'),            // TILT ≥50 alla chiusura → +2 mult
  def('risk-vertigine', 'rischio', 'raro'),         // +1 mult ogni 25 di TILT
  def('risk-fenice', 'rischio', 'raro'),            // mano travolta dal caos → +40 chips
  def('risk-berserk', 'rischio', 'leggendario'),    // TILT pieno: caos a favore + 2 mult
  def('risk-abisso', 'rischio', 'leggendario'),     // TILT ≥70 alla chiusura → mult ×2
];

export const TALISMAN_BY_ID = new Map(TALISMANS.map((t) => [t.id, t]));

export interface ScoreCtx {
  talismans: string[];
  kinds: DieKind[];
  /** Rilanci forzati usati in questa mano (caricano il TILT). */
  forcedRerolls: number;
  /** Rilanci liberi usati in questa mano. */
  freeRerolls: number;
  /** Valore della barra TILT alla chiusura (0..100). */
  tilt: number;
  /** La mano ha subìto (o cavalcato) il lancio caotico del TILT pieno. */
  tiltChaos: boolean;
  /** Round corrente (1-based). */
  round: number;
  /** Mani rimaste PRIMA di chiudere questa (3 = prima mano, 1 = ultima). */
  handsLeft: number;
  /** Regola boss attiva sul round (null fuori dai boss round). */
  bossRule: BossRule | null;
}

export interface ScoredHandFull {
  best: ComboResult;
  all: ComboResult[];
  chips: number;
  mult: number;
  score: number;
  /** Talismani che hanno effettivamente contribuito (per il flash in UI). */
  triggered: string[];
}

const MONO_COMBOS = new Set(['three-kind', 'four-kind', 'five-kind', 'six-kind']);
const PAIR_COMBOS = new Set(['pair', 'two-pair', 'full-house']);
const STRAIGHT_COMBOS = new Set(['small-straight', 'large-straight']);
const TOP_COMBOS = new Set(['five-kind', 'six-kind']);

export function scoreHandCtx(faces: number[], ctx: ScoreCtx): ScoredHandFull {
  const has = (id: string) => ctx.talismans.includes(id);
  const triggered = new Set<string>();

  // 1. pips truccati (talismani, Dado di Vetro, boss "no-sei")
  const pip: PipValue = (face, index) => {
    let v = face;
    if (face === 3 && has('fix-tre')) v = 6;
    if (face === 1 && has('alch-metallo')) v = 7;
    if (ctx.bossRule === 'no-sei' && face === 6) v = 0;
    if (ctx.kinds[index] === 'vetro') v *= 2;
    return v;
  };

  // 2. combo migliore (boss "minimo-tris": le coppie non contano)
  let all = detectCombos(faces, pip);
  if (ctx.bossRule === 'minimo-tris') {
    all = all.filter((c) => c.id !== 'pair' && c.id !== 'two-pair');
  }
  const best = all[0]!;
  const used = best.usedIndices;
  const unused = faces.map((_, i) => i).filter((i) => !used.includes(i));
  if (has('fix-tre') && used.some((i) => faces[i] === 3)) triggered.add('fix-tre');
  if (has('alch-metallo') && used.some((i) => faces[i] === 1)) triggered.add('alch-metallo');

  // 3. chips flat
  let chips = best.chips;
  const addChips = (id: string, amount: number): void => {
    if (amount === 0) return;
    chips += amount;
    triggered.add(id);
  };
  if (has('fix-pari')) addChips('fix-pari', 4 * used.filter((i) => (faces[i] ?? 0) % 2 === 0).length);
  if (has('fix-dispari')) addChips('fix-dispari', 3 * used.filter((i) => (faces[i] ?? 0) % 2 === 1).length);
  if (has('fix-cinque')) addChips('fix-cinque', 5 * used.filter((i) => faces[i] === 5).length);
  if (has('fix-doppietta') && best.id === 'two-pair') addChips('fix-doppietta', 25);
  if (has('pyro-sei')) addChips('pyro-sei', 8 * used.filter((i) => faces[i] === 6).length);
  if (has('pyro-polvere') && ctx.forcedRerolls > 0) addChips('pyro-polvere', 20);
  if (has('pyro-lampo') && ctx.handsLeft === HANDS_PER_ROUND) addChips('pyro-lampo', 15);
  if (has('ghost-dado') && PAIR_COMBOS.has(best.id)) addChips('ghost-dado', 15);
  if (has('ghost-nebbia') && used.length < faces.length) addChips('ghost-nebbia', 10);
  if (has('ghost-sussurro')) {
    addChips('ghost-sussurro', Math.floor(unused.reduce((s, i) => s + pip(faces[i] ?? 0, i), 0) / 2));
  }
  if (has('ghost-terzo') && best.id === 'three-kind') addChips('ghost-terzo', 30);
  if (has('ghost-specchi')) {
    const counts = new Map<number, number>();
    for (const f of faces) counts.set(f, (counts.get(f) ?? 0) + 1);
    const pairs = [...counts.values()].reduce((s, n) => s + Math.floor(n / 2), 0);
    addChips('ghost-specchi', 4 * pairs);
  }
  if (has('ghost-eco')) {
    addChips('ghost-eco', Math.max(...used.map((i) => pip(faces[i] ?? 0, i))));
  }
  if (has('alch-mercurio')) addChips('alch-mercurio', used.length);
  if (has('alch-fermento')) addChips('alch-fermento', 2 * (ctx.round - 1));
  if (has('alch-piombo-oro')) {
    addChips('alch-piombo-oro', 6 * used.filter((i) => (ctx.kinds[i] ?? 'standard') !== 'standard').length);
  }
  if (has('risk-adrenalina') && ctx.tilt >= 35) addChips('risk-adrenalina', 12);
  if (has('risk-cerino') && ctx.handsLeft === 1) addChips('risk-cerino', 20);
  if (has('risk-fenice') && ctx.tiltChaos) addChips('risk-fenice', 40);
  chips += BOMB_BONUS * used.filter((i) => ctx.kinds[i] === 'bomba' && faces[i] === 6).length;
  chips += EMBER_BONUS * used.filter((i) => ctx.kinds[i] === 'brace').length;

  // 4. chips percentuali
  if (has('alch-pietra')) {
    chips = Math.round(chips * 1.25);
    triggered.add('alch-pietra');
  }

  // 5. mult additivi
  let mult = 1;
  const addMult = (id: string, amount: number): void => {
    if (amount === 0) return;
    mult += amount;
    triggered.add(id);
  };
  if (has('fix-mono') && MONO_COMBOS.has(best.id)) addMult('fix-mono', 2);
  if (has('fix-scala') && STRAIGHT_COMBOS.has(best.id)) addMult('fix-scala', 2);
  if (has('pyro-miccia') && ctx.freeRerolls === 0 && ctx.forcedRerolls === 0) addMult('pyro-miccia', 2);
  if (has('pyro-catena')) addMult('pyro-catena', 0.5 * (ctx.freeRerolls + ctx.forcedRerolls));
  if (has('pyro-castello') && best.id === 'full-house') addMult('pyro-castello', 2);
  if (has('alch-distillato') && used.length === faces.length) addMult('alch-distillato', 1.5);
  if (has('alch-enzima') && used.length === 4) addMult('alch-enzima', 1.5);
  if (has('alch-grande-opera')) {
    const families = new Set(ctx.talismans.map((id) => TALISMAN_BY_ID.get(id)?.family));
    addMult('alch-grande-opera', 0.75 * families.size);
  }
  if (has('ghost-medium')) addMult('ghost-medium', unused.length);
  if (has('ghost-presenza')) addMult('ghost-presenza', 0.5 * ctx.talismans.length);
  if (has('risk-freno')) addMult('risk-freno', ctx.forcedRerolls);
  if (has('risk-limite') && ctx.tilt >= 50) addMult('risk-limite', 2);
  if (has('risk-vertigine')) addMult('risk-vertigine', ctx.tilt / 25);
  if (has('risk-berserk') && ctx.tiltChaos) addMult('risk-berserk', 2);

  // 6. mult moltiplicativi
  const timesMult = (id: string, factor: number): void => {
    mult *= factor;
    triggered.add(id);
  };
  if (has('fix-tabellina') && used.length >= 2 && used.every((i) => (faces[i] ?? 0) % 2 === 0)) {
    timesMult('fix-tabellina', 1.5);
  }
  if (has('pyro-finale') && best.base >= 120) timesMult('pyro-finale', 2);
  if (has('pyro-apoteosi') && TOP_COMBOS.has(best.id)) timesMult('pyro-apoteosi', 3);
  if (has('risk-abisso') && ctx.tilt >= 70) timesMult('risk-abisso', 2);

  mult = Math.round(mult * 10) / 10;
  const score = Math.round(chips * mult);
  return { best, all, chips, mult, score, triggered: [...triggered] };
}
